import React, { useEffect, useCallback, useRef, useState } from 'react';
import {
    Box,
    Typography,
    Link as MuiLink,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
} from '@mui/material';
import { Link } from 'react-router-dom';
import PoweredByDQDefault from '../../../assets/images/PoweredByDQ_Default.png';
import PoweredByDQWhite from '../../../assets/images/PoweredByDQ_white.png';
import useStyles from '../../../assets/styles/CurationPromptApplicationStyles';
import { useCurationApplicationStream } from './hooks/useCurationApplicationStream';
import { useColorMode } from '../../../context/colorMode/colorMode.context';
import CpaChatPanel from './components/CpaChatPanel.comp';
import CpaPackagePanel from './components/CpaPackagePanel.comp';
import CpaFloatingChatWidget from './components/CpaFloatingChatWidget.comp';

const CurationPromptApplicationPage = () => {
    const classes = useStyles();
    const { isDark } = useColorMode();
    const PoweredByDQ = isDark ? PoweredByDQWhite : PoweredByDQDefault;
    const widgetRef = useRef(null);
    const [panelTab, setPanelTab] = useState(0);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const {
        chatHistory,
        uiState,
        isStreaming,
        initSession,
        resetSession,
        abandonSession,
        submitOrgType,
        submitGoals,
        submitQuoteResponse,
        submitContactForm,
        sendChatMessage,
    } = useCurationApplicationStream();

    useEffect(() => {
        initSession();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

   
    // start so they can explore packages again — not a blank screen.
    const handleCancelRequest = useCallback(async () => {
        setShowCancelDialog(true);
    }, []);

    const handleConfirmCancelRequest = useCallback(async () => {
        setShowCancelDialog(false);
        await abandonSession();   // end old session on the server + clear local cache
        await initSession();      // fresh start → back to the package exploration flow
    }, [abandonSession, initSession]);

    const handlePanelRequestQuote = useCallback(() => {
        if (uiState.applicationSubmitted || uiState.currentForm === 'contact_form') return;
        // Make sure the chat is open so the user sees the contact form.
        if (widgetRef.current) widgetRef.current.open();
        if (uiState.showQuotePrompt) {
            submitQuoteResponse(true, "Yes, I'd like a quote");
        }
    }, [uiState.applicationSubmitted, uiState.currentForm, uiState.showQuotePrompt, submitQuoteResponse]);

    return (
        <Box className={classes.pageWrapper}>
            <Box className={classes.header}>
                <MuiLink component={Link} to="/my_workspace" style={{ lineHeight: 0, flexShrink: 0 }}>
                    <img src={PoweredByDQ} alt="Powered by DQ" className={classes.logo} />
                </MuiLink>

                <Box className={classes.headerCenter}>
                    <Typography style={{ fontWeight: 800, fontSize: 15, color: 'var(--brand-text, #1a1a1a)', lineHeight: 1.2, letterSpacing: '0.01em' }}>
                        Find Your Perfect Package
                    </Typography>
                    <Typography style={{ fontWeight: 400, fontSize: 12, color: 'var(--brand-text-muted, #888)', lineHeight: 1.3, marginTop: 2 }}>
                        AI &amp; Digital Literacy Solutions by DQ
                    </Typography>
                </Box>

                <Box className={classes.headerRight}>
                    <Typography variant="body2" style={{ color: 'var(--brand-text-muted, #555)', whiteSpace: 'nowrap' }}>
                        Already have an account?{' '}
                        <MuiLink component={Link} to="/login" color="primary" style={{ fontWeight: 600 }}>Log in</MuiLink>
                    </Typography>
                </Box>
            </Box>

            {/* Main content area — the recommended package appears here as the flow
                progresses; the chat itself lives in the floating widget. */}
            <Box className={classes.mainRow}>
                <Box className={classes.panelColumn}>
                    <CpaPackagePanel
                        visible={uiState.showPackagePanel}
                        packageCode={uiState.recommendedPackage}
                        showQuoteButton={uiState.showQuotePrompt && !uiState.applicationSubmitted}
                        onRequestQuote={handlePanelRequestQuote}
                        onTabChange={setPanelTab}
                    />
                </Box>
            </Box>

            {/* Floating chat widget — identical look/motion to the program-creation
                widget; hosts the Curation Prompt Application flow. */}
            <CpaFloatingChatWidget ref={widgetRef} startOpen hidden={panelTab !== 0}>
                <CpaChatPanel
                    classes={classes}
                    chatHistory={chatHistory}
                    isStreaming={isStreaming}
                    uiState={uiState}
                    onSubmitOrgType={submitOrgType}
                    onSubmitGoals={submitGoals}
                    onSubmitQuoteResponse={submitQuoteResponse}
                    onSubmitContactForm={submitContactForm}
                    onReset={resetSession}
                    onCancelRequest={handleCancelRequest}
                    onSendMessage={sendChatMessage}
                />
            </CpaFloatingChatWidget>

            <Dialog
                open={showCancelDialog}
                onClose={() => setShowCancelDialog(false)}
                PaperProps={{ style: { borderRadius: 14, backgroundColor: 'var(--brand-surface, #fff)' } }}
            >
                <DialogTitle sx={{ fontWeight: 700, color: 'var(--brand-text, #000)' }}>
                    Heads up!
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: 'var(--brand-text-muted, #555)' }}>
                        Cancel this request and return to package exploration? Your current draft form will be cleared.
                    </DialogContentText>
                </DialogContent>
                <DialogActions style={{ padding: '8px 20px 20px' }}>
                    <Button
                        onClick={() => setShowCancelDialog(false)}
                        sx={{ color: 'var(--brand-text-muted, #555)', textTransform: 'none' }}
                    >
                        Keep Editing
                    </Button>
                    <Button
                        onClick={handleConfirmCancelRequest}
                        variant="contained"
                        style={{ backgroundColor: '#0397a6', color: '#fff', textTransform: 'none' }}
                    >
                        Yes, Cancel Request
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CurationPromptApplicationPage;
